import MobileOtpInput from 'react-otp-input';
import {Button, Text} from '@kidslabs/mario-nextjs-react-ui-core';
import {Form} from '@/_application/core/components/ui/forms/form';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'next-i18next';
import {isMobile} from 'react-device-detect';
import React, {useState, useEffect} from 'react';
import {useAtom} from 'jotai';
import {otpRequestedTimeAtom} from '@/_application/modules/auth/stores/atom';
import type { OptCodeFormProps, OtpLoginFormForAllUserProps } from './otp-login.types';
import { otpLoginFormSchemaForExistingUser } from './otp-login.schema';
import { diffInSeconds } from '../../utils';

import styles from './otp.module.css';

export default function OtpCodeForm({onSubmit, isLoading, resendOtp}: Readonly<OtpLoginFormForAllUserProps>) {
    const {t} = useTranslation('common');
    const [otpRequestedTime] = useAtom(otpRequestedTimeAtom);
    const [counter, setCounter] = useState(120);
    const [isResendOtp, setIsResendOtp] = useState(false);
    const [resetValues, setResetValues] = useState<OptCodeFormProps>();

    useEffect(() => {
        const initialCounter = 120 - diffInSeconds(new Date(), new Date(otpRequestedTime));
        setCounter(Math.max(initialCounter, 0));
    }, [otpRequestedTime]);

    useEffect(() => {
        const timerOTP = counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
        if (counter === 0) {
            setIsResendOtp(false);
        }
        return () => {
            timerOTP && clearTimeout(timerOTP);
        };
    }, [counter]);

    const handleResendOtp = () => {
        if (resendOtp && typeof resendOtp === 'function' && !isResendOtp) {
            resendOtp();
            setIsResendOtp(true);
            setResetValues({code: ''}); //clear current otp input when user hit resend otp
        }
    };

    return (
        <div className={isMobile ? '' : 'space-y-5 rounded p-5'}>
            <Form<OptCodeFormProps>
                onSubmit={onSubmit}
                validationSchema={otpLoginFormSchemaForExistingUser}
                resetValues={resetValues}>
                {({control, formState: {errors}}) => (
                    <>
                        <div className="mx-2 mb-5">
                            <div className="pb-4 text-lg font-bold text-primary">{t('text-otp-code')}</div>
                            <div className="pb-6">
                                <Controller
                                    control={control}
                                    render={({field: {onChange, value}}) => (
                                        <MobileOtpInput
                                            shouldAutoFocus
                                            isInputNum
                                            value={value}
                                            onChange={onChange}
                                            numInputs={6}
                                            containerStyle="flex items-center justify-start"
                                            inputStyle={`${
                                                isMobile ? 'w-full!' : 'w-12!'
                                            } !md:w-full bg-input-otp flex items-center justify-center mx-2 px-0! appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0 border border-none rounded-lg focus:border-accent h-12 bg-gray-200`}
                                            disabledStyle="bg-gray-100!"
                                        />
                                    )}
                                    name="code"
                                    defaultValue=""
                                />
                                {errors.code?.message && (
                                    <p className="mb-2 mt-2 text-xs text-red-500 ltr:text-left rtl:text-right">
                                        {t(errors.code.message)}
                                    </p>
                                )}
                            </div>
                            {counter === 0 ? (
                                <div className="flex items-center justify-center">
                                    <span className="mx-2 text-sm">{t('text-get-otp')}</span>
                                    <Button
                                        className="cursor-pointer text-sm text-blue-kid shadow-none!"
                                        onClick={handleResendOtp}>
                                        {t('text-btn-resend-otp')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <span className="mx-2 text-sm">{t('text-resend-otp')}</span>
                                    <span className="text-sm text-blue-kid">
                                        {t('text-otp-limited-time', {second: counter})}
                                    </span>
                                    <span className="mx-2 text-sm">{t('text-resend-otp-code')}</span>
                                </div>
                            )}
                        </div>
                        <div className="w-full">
                            <Button
                                loading={isLoading}
                                disabled={isLoading}
                                aria-label="Xác minh mã"
                                className="w-full bg-primary uppercase hover:bg-blue-kid! shadow-none!">
                                <Text className={styles.buttonText}>{t('text-verify-code')}</Text>
                            </Button>
                        </div>
                    </>
                )}
            </Form>
        </div>
    );
}
